import React, {useEffect, useRef, useState} from "react";
import {useHistory, useLocation} from "react-router";
import * as io from "socket.io-client";
import constants from "../../../config/constants";
import Box from "@mui/material/Box";
import {useSelector} from "react-redux";
import {selectSessionUser} from "../../../store/authentication/selectors";
import "./game-generator.css";
import RulesButtons from "./RulesButtons";


export const GameGeneratorPage = () => {


    const location = useLocation();
    const user = useSelector(selectSessionUser);
    const socketRef = useRef(null); // useRef for socket
    const history = useHistory();
    const lobbyId = location.pathname.split("/")[2];
    const mediaRecorderRef = useRef(null);
    const [listening, setListening] = useState(false);
    const [isAdminUser, setIsAdminUser] = useState(false);
    const [gameRedirectUrl, setGameRedirectUrl] = useState("");
    const [gameStarted, setGameStarted] = useState(false);
    const [selectedColor, setSelectedColor] = useState(null); // null means WHITE (default)
    const [generationProgress, setGenerationProgress] = useState(undefined);
    const listeningRef = useRef(listening);
    const isAdminUserRef = useRef(isAdminUser);
    const [lobby, setLobby] = useState(null);
    const [hasAudioPermission, setHasAudioPermission] = useState(false);
    const [microphoneStream, setMicrophoneStream] = useState(null);

    // Color options for the selector
    const colors = [
        {name: 'RED', value: '#FF4444'},
        {name: 'BLUE', value: '#4444FF'},
        {name: 'GREEN', value: '#44FF44'},
        {name: 'YELLOW', value: '#FFFF44'},
        {name: 'ORANGE', value: '#FF8844'},
        {name: 'PURPLE', value: '#8844FF'}
    ];

    // Keep listeningRef in sync with listening state
    useEffect(() => {
        listeningRef.current = listening;
    }, [listening]);

    useEffect(() => {
        if (user) {
            socketRef.current = io.connect(constants.api.socketPath, {transports: ['websocket']});
            const socket = socketRef.current;
            socket.on('gg:lobby', function (data) {
                if (!isAdminUserRef.current && data.adminUserId === user._id) {
                    setIsAdminUser(true);
                    isAdminUserRef.current = true;
                }
                setLobby(data);
                console.log("Lobby data:", data);
            });
            socket.connect();
            socket.on('disconnect', function () {
                if (!window.location.pathname.startsWith("/generated")) {
                    history.push("/");
                }
            });
            socket.on("gg:game-started", (data) => {
                const res = JSON.parse(data);
                setGameRedirectUrl("/generated/" + res.gameId +"/" + res.lobbyId);
            });
            socket.on('games', (data) => {
                if(data.games){
                    for(let game of data.games){
                        if(game.lobbyId === lobbyId){
                            setGameStarted(true);
                        }
                    }
                }
            });
            socket.emit('gg:id', JSON.stringify({type: "PLAYER", lobbyId, userId: user._id, username: user.username}));
            if (localStorage.getItem("generated-color")) {
                const color = colors.find(c => c.name === localStorage.getItem("generated-color"))
                if (color) {
                    handleColorSelect(color);
                }
            }
            return () => {
                socket.close();
                if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
                    mediaRecorderRef.current.stop();
                }
            }
        }
    }, [user]);

    useEffect(() => {
        if(gameStarted && gameRedirectUrl) {
            history.push(gameRedirectUrl);
        }
    }, [gameRedirectUrl, gameStarted])

    // Request microphone permission when component mounts
    useEffect(() => {
        const requestMicrophonePermission = async () => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({audio: true});
                setHasAudioPermission(true);
                setMicrophoneStream(stream);
                console.log("Microphone permission granted");
            } catch (err) {
                console.error("Error requesting microphone permission:", err);
                setHasAudioPermission(false);
            }
        };

        requestMicrophonePermission();

        // Cleanup function to release microphone when component unmounts
        return () => {
            if (microphoneStream) {
                microphoneStream.getTracks().forEach(track => track.stop());
            }
        };
    }, []);

    const startListening = async () => {
        try {
            let stream;

            if (microphoneStream && microphoneStream.active) {
                // Use existing stream if available
                stream = microphoneStream;
            } else {
                // Request permission again if stream was lost
                stream = await navigator.mediaDevices.getUserMedia({audio: true});
                setMicrophoneStream(stream);
                setHasAudioPermission(true);
            }

            let mimeType;
            if (MediaRecorder.isTypeSupported("audio/webm")) {
                mimeType = "audio/webm";
            } else if (MediaRecorder.isTypeSupported("audio/mp4")) {
                mimeType = "audio/mp4";
            } else if (MediaRecorder.isTypeSupported("audio/aac")) {
                mimeType = "audio/aac";
            } else if (MediaRecorder.isTypeSupported("audio/wav")) {
                mimeType = "audio/wav";
            } else {
                mimeType = ""; // fallback, let browser choose
            }

            setListening(true);
            mediaRecorderRef.current = new MediaRecorder(stream, {mimeType});
            mediaRecorderRef.current.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    e.data.arrayBuffer().then((buf) => {
                        socketRef.current.emit("gg:player-chat-chunk", buf);
                    });
                }
            };
            mediaRecorderRef.current.start(250); // send chunk every 250ms
        } catch (err) {
            console.error("Error starting recording:", err);
            setHasAudioPermission(false);
        }
    };

    const stopListening = () => {
        // Stop the recorder, which triggers onstop and emits final message
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
            mediaRecorderRef.current.stop();
            setTimeout(() => {
                socketRef.current.emit("gg:player-chat-send", "coucou");
                setListening(false);
            }, 500)
        }
    };

    const handleColorSelect = (color) => {
        setSelectedColor(color.value);
        localStorage.setItem("generated-color", color.name);
        socketRef.current.emit("gg:player-info", {color: color.value});
    };

    const handleShowQRCode = () => {
        socketRef.current.emit("gg:display-qr-code");
    };

    const handleDisplayRule = (ruleIndex) => {
        console.log("Displaying rule:", ruleIndex);
        socketRef.current.emit("gg:display-rule", { ruleIndex });
    };

    const handleStartGame = () => {
        console.log("Start game clicked");
        socketRef.current.emit("gg:start-generation");
    };

    const generationInProgress = !!lobby && lobby.generationStarted;
    const isMicDisabled = !hasAudioPermission || (generationProgress && generationProgress.overallStatus !== "not_started") || (lobby && lobby.generationStarted);

    if (!lobby) {
        return undefined;
    }

    return (
        <div style={{
            position: "relative",
            height: "100vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column"
        }}>
            <Box
                className="board-game"
                sx={{
                    width: {xs: "80%", md: "50%", lg: "33%"},
                    margin: "0 auto",
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    paddingBottom: "100px" // Space for bottom color selector
                }}
            >

                <div style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "center"
                }}>
                    <div className="header-lobby" style={{}}>
                        <div className="title">Game on!</div>
                        <div className="subtitle">À quoi jouons-nous aujourd'hui ?</div>
                    </div>
                    {
                        generationInProgress ?
                            <div style={{
                                width: '100%',
                                textAlign: 'center',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center'
                            }}>
                                <div>Génération en cours...</div>
                                {/* Display rules buttons if rulesModel is available in generationProgress */}
                                {isAdminUser && lobby?.generationProgress?.rulesModel && (
                                    <RulesButtons
                                        rulesModel={lobby.generationProgress.rulesModel.rules}
                                        onDisplayRule={handleDisplayRule}
                                    />
                                )}
                            </div>
                        :
                            <div>
                                <div className={`mic-wrap ${listening ? "listening" : ""}`} aria-hidden>
                                    <div className="pulse-ring" aria-hidden>
                                        <div className="ring r3" style={
                                            listening && selectedColor
                                                ? {background: selectedColor}
                                                : undefined
                                        }/>
                                        <div className="ring r2" style={
                                            listening && selectedColor
                                                ? {background: selectedColor}
                                                : undefined
                                        }/>
                                        <div className="ring r1" style={
                                            listening && selectedColor
                                                ? {background: selectedColor}
                                                : undefined
                                        }/>
                                    </div>
                                    <button
                                        className={`mic-btn ${isMicDisabled ? 'disabled' : ''}`}
                                        onMouseDown={isMicDisabled ? null : startListening}
                                        onMouseUp={stopListening}
                                        onMouseLeave={stopListening}
                                        onTouchStart={isMicDisabled ? null : startListening}
                                        onTouchEnd={stopListening}
                                        aria-pressed={listening}
                                        disabled={isMicDisabled}
                                        aria-label={isMicDisabled ? "Microphone désactivé" : (listening ? "Arrêter l'enregistrement" : "Commencer l'enregistrement")}
                                        title={isMicDisabled ? "Microphone désactivé" : (listening ? "Arrêter" : "Parler")}
                                        style={{
                                            position: "relative",
                                            zIndex: 2,
                                            opacity: isMicDisabled ? 0.6 : 1,
                                            cursor: isMicDisabled ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        {/* SVG microphone */}
                                        <svg xmlns="http://www.w3.org/2000/svg" width="120" height="120"
                                             viewBox="0 0 24 24"
                                             fill="none">
                                            <defs>
                                                <linearGradient id="grad" x1="0%" y1="0%" x2="0%" y2="100%">
                                                    <stop offset="0%" stopColor={listening ? "#efeafc" : "#a3a5fb"}/>
                                                    <stop offset="100%" stopColor={listening ? "#a3a5fb" : "#7b78ff"}/>
                                                </linearGradient>
                                            </defs>
                                            <path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3z"
                                                  fill="url(#grad)"/>
                                            <path
                                                d="M19 11a1 1 0 1 0-2 0 5 5 0 1 1-10 0 1 1 0 1 0-2 0 7 7 0 0 0 6 6.92V20H9a1 1 0 1 0 0 2h6a1 1 0 1 0 0-2h-2v-2.08A7 7 0 0 0 19 11z"
                                                fill="url(#grad)"/>
                                        </svg>
                                    </button>
                                    <div style={{
                                        textAlign: "center",
                                        marginTop: "1vh",
                                        color: "var(--muted)",
                                        fontSize: "clamp(14px, 3vw, 16px)"
                                    }}>
                                    </div>
                                </div>
                                <div style={{width: "100%", textAlign: "center"}}>
                                    Laissez appuyé pour parler
                                </div>
                            </div>
                    }


                    {/* Admin controls - Only visible for admin users */}
                    {isAdminUser && (
                        <div className="admin-controls" style={{
                            display: "flex",
                            justifyContent: "center",
                            gap: "16px",
                            marginTop: "2vh"
                        }}>
                            <button
                                className="icon-btn admin-btn qr-btn"
                                onClick={handleShowQRCode}
                                style={{
                                    width: "64px",
                                    height: "64px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: "16px",
                                    background: "linear-gradient(135deg, rgba(90,88,255,0.1), rgba(43,72,255,0.05))",
                                    border: "1px solid rgba(90,88,255,0.3)",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    color: "var(--accent)",
                                    boxShadow: "0 4px 16px rgba(90,88,255,0.2)",
                                    position: "relative"
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.transform = "translateY(-2px)";
                                    e.target.style.boxShadow = "0 6px 20px rgba(90,88,255,0.3)";
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.transform = "translateY(0px)";
                                    e.target.style.boxShadow = "0 4px 16px rgba(90,88,255,0.2)";
                                }}
                                aria-label="Afficher le QR code"
                                title="Afficher le QR code"
                            >
                                {/* QR Code SVG Icon - Bigger */}
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"
                                     fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                     strokeLinejoin="round">
                                    <rect x="3" y="3" width="5" height="5"/>
                                    <rect x="3" y="16" width="5" height="5"/>
                                    <rect x="16" y="3" width="5" height="5"/>
                                    <path d="M21 16h-3a2 2 0 0 0-2 2v3"/>
                                    <path d="M21 21v.01"/>
                                    <path d="M12 7v3a2 2 0 0 1-2 2H7"/>
                                    <path d="M3 12h.01"/>
                                    <path d="M12 3h.01"/>
                                    <path d="M12 16v.01"/>
                                    <path d="M16 12h1"/>
                                    <path d="M21 12v.01"/>
                                    <path d="M12 21v-1"/>
                                </svg>
                            </button>

                            <button
                                className="icon-btn admin-btn play-btn"
                                onClick={handleStartGame}
                                disabled={!lobby.complete || generationInProgress}
                                style={{
                                    width: "64px",
                                    height: "64px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: "16px",
                                    background: (!lobby.complete || generationInProgress)
                                        ? "linear-gradient(135deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01))"
                                        : "linear-gradient(135deg, rgba(90,88,255,0.1), rgba(43,72,255,0.05))",
                                    border: (!lobby.complete || generationInProgress)
                                        ? "1px solid rgba(255,255,255,0.1)"
                                        : "1px solid rgba(90,88,255,0.3)",
                                    cursor: (!lobby.complete || generationInProgress) ? "not-allowed" : "pointer",
                                    transition: "all 0.2s ease",
                                    color: (!lobby.complete || generationInProgress) ? "var(--muted)" : "var(--accent)",
                                    opacity: (!lobby.complete || generationInProgress) ? "0.6" : "1",
                                    boxShadow: (!lobby.complete || generationInProgress)
                                        ? "0 4px 16px rgba(2,3,10,0.3)"
                                        : "0 4px 16px rgba(90,88,255,0.2)",
                                    position: "relative"
                                }}
                                onMouseEnter={(e) => {
                                    if (lobby.complete && !generationInProgress) {
                                        e.target.style.transform = "translateY(-2px)";
                                        e.target.style.boxShadow = "0 6px 20px rgba(90,88,255,0.3)";
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (lobby.complete && !generationInProgress) {
                                        e.target.style.transform = "translateY(0px)";
                                        e.target.style.boxShadow = "0 4px 16px rgba(90,88,255,0.2)";
                                    }
                                }}
                                aria-label={(!lobby.complete || generationInProgress)
                                    ? "Démarrer le jeu (désactivé)"
                                    : "Démarrer le jeu"}
                                title={(!lobby.complete || generationInProgress)
                                    ? "Démarrer le jeu (bientôt disponible)"
                                    : "Démarrer le jeu"}
                            >
                                {/* Play SVG Icon - Bigger */}
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"
                                     fill="currentColor">
                                    <path d="M8 5v14l11-7z"/>
                                </svg>

                                {/* Disabled overlay effect - only show when disabled */}
                                {
                                    (!lobby.complete || generationInProgress) ?
                                        <div style={{
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            borderRadius: "16px",
                                            background: "repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(255,255,255,0.05) 2px, rgba(255,255,255,0.05) 4px)",
                                            pointerEvents: "none"
                                        }}/> : undefined
                                }
                            </button>
                        </div>
                    )}
                </div>
            </Box>

            {/* Color selector - Fixed at bottom of screen */}
            <div className="color-selector-bottom" style={{
                position: "fixed",
                bottom: "0",
                left: "0",
                right: "0",
                background: "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))",
                padding: "20px",
                boxShadow: "0 -8px 24px rgba(2,3,10,0.6)",
                border: "1px solid rgba(255,255,255,0.06)",
                borderBottom: "none",
                zIndex: 1000
            }}>
                {
                    !selectedColor ? <div style={{
                        textAlign: "center",
                        marginBottom: "12px",
                        color: "white",
                        fontSize: "clamp(14px, 3vw, 16px)"
                    }}>
                        Choisissez votre couleur
                    </div> : undefined
                }
                <div style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "12px",
                    maxWidth: "600px",
                    margin: "0 auto"
                }}>
                    {colors.map((color) => (
                        <button
                            key={color.name}
                            className={`color-swatch ${selectedColor === color.value ? "selected" : ""}`}
                            onClick={() => handleColorSelect(color)}
                            style={{
                                backgroundColor: color.value,
                                width: "48px",
                                height: "48px",
                                border: selectedColor === color.value ? "3px solid #5a58ff" : "2px solid rgba(255,255,255,0.12)",
                                borderRadius: "12px",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                boxShadow: selectedColor === color.value
                                    ? "0 0 0 2px rgba(90,88,255,0.3), 0 4px 12px rgba(43,72,255,0.4)"
                                    : "0 2px 8px rgba(2,3,10,0.3)",
                                transform: selectedColor === color.value ? "scale(1.1)" : "scale(1)",
                                opacity: selectedColor !== color.value ? 0.4 : 1,
                                filter: selectedColor !== color.value ? "grayscale(0.3)" : "none"
                            }}
                            aria-label={`Sélectionner la couleur ${color.name}`}
                            title={color.name}
                        />
                    ))}
                </div>
            </div>

            {/* Add permission warning if microphone access was denied */}
            {!hasAudioPermission && (
                <div style={{
                    position: "fixed",
                    top: "20px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: "rgba(255, 100, 100, 0.9)",
                    color: "white",
                    padding: "12px 20px",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                    zIndex: 1100,
                    maxWidth: "90%",
                    textAlign: "center"
                }}>
                    <p style={{margin: 0}}>
                        Veuillez autoriser l'accès au microphone pour pouvoir parler
                    </p>
                </div>
            )}
        </div>
    )
};
